/**
 * Preset mappings for the Vertical Spread Simulator.
 *
 * Mirrors the shape of presetMappings.js (which drives the screener) but is a
 * separate module: the spread form asks a different question — pick an anchor
 * leg and a width, not a single strike — and the screener's presets must not
 * shift underneath it. Expiration and anchor presets are imported READ-ONLY
 * from presetMappings so the two tools stay visually consistent.
 */
import { intermediatePresets, formatTickersForAPI } from './presetMappings';

export { formatTickersForAPI };

/**
 * The four vertical spreads.
 *
 * `anchor` is the leg nearest the money — the one the user positions. Whether
 * that leg is bought or sold is what makes the spread a debit or a credit, and
 * that in turn is what sets the directional bias:
 *   - buying the nearer (pricier) leg  -> pay a DEBIT, want the spread to widen
 *   - selling the nearer leg           -> collect a CREDIT, want it to narrow
 *
 * Keys match the backend's SPREAD_TYPES exactly.
 */
export const SPREAD_TYPES = {
  call_debit: {
    key: 'call_debit',
    label: 'Call Debit Spread',
    shortLabel: 'Call Debit',
    direction: 'Bullish',
    optionType: 'call',
    entryType: 'Debit',
    isCredit: false,
    subtitle: 'Buy a call, sell a higher call',
    tooltip:
      'Bullish. You pay a debit and profit as the stock rises. Cheaper than '
      + 'buying the call outright, but your upside stops at the short strike.',
    riskNote: 'Max loss = debit paid',
  },
  put_credit: {
    key: 'put_credit',
    label: 'Put Credit Spread',
    shortLabel: 'Put Credit',
    direction: 'Bullish',
    optionType: 'put',
    entryType: 'Credit',
    isCredit: true,
    subtitle: 'Sell a put, buy a lower put',
    tooltip:
      'Bullish. You collect a credit up front and keep it if the stock stays '
      + 'above the short strike. Time decay works for you; the long put caps the loss.',
    riskNote: 'Max loss = width − credit',
  },
  put_debit: {
    key: 'put_debit',
    label: 'Put Debit Spread',
    shortLabel: 'Put Debit',
    direction: 'Bearish',
    optionType: 'put',
    entryType: 'Debit',
    isCredit: false,
    subtitle: 'Buy a put, sell a lower put',
    tooltip:
      'Bearish. You pay a debit and profit as the stock falls. Cheaper than '
      + 'buying the put outright, with downside capped at the short strike.',
    riskNote: 'Max loss = debit paid',
  },
  call_credit: {
    key: 'call_credit',
    label: 'Call Credit Spread',
    shortLabel: 'Call Credit',
    direction: 'Bearish',
    optionType: 'call',
    entryType: 'Credit',
    isCredit: true,
    subtitle: 'Sell a call, buy a higher call',
    tooltip:
      'Bearish. You collect a credit and keep it if the stock stays below the '
      + 'short strike. The long call caps what an upside surprise can cost you.',
    riskNote: 'Max loss = width − credit',
  },
};

/** Render order for the 2x2 grid: bullish row, then bearish row. */
export const SPREAD_TYPE_ORDER = [
  'call_debit', 'put_credit',
  'put_debit', 'call_credit',
];

/**
 * Expiration windows. Read-only reuse of the screener's presets so the labels
 * and tooltips match what users already know; keys match the backend's
 * EXPIRATION_WINDOWS.
 */
export const spreadExpirationPresets = intermediatePresets.expiration;

/**
 * Anchor strike positions for the near-the-money leg, in CALL convention
 * (negative = ITM for calls). The backend flips the sign for puts, exactly as
 * buildIntermediateScenarios does for the screener.
 *
 * A narrower band than the screener's strike presets: a vertical spread needs a
 * second strike beyond the anchor, so far-OTM anchors (+50%, +100%) leave no
 * room for a width and are omitted.
 */
export const spreadAnchorPresets = {
  'itm-20': { anchor_pct: -0.20, label: 'Deep ITM', description: '-20%', tooltip: 'Anchor deep in the money. Highest cost and highest probability; behaves most like the stock.' },
  'itm-10': { anchor_pct: -0.10, label: 'ITM', description: '-10%', tooltip: 'Anchor in the money. Higher debit, higher probability of finishing profitable.' },
  'itm-5': { anchor_pct: -0.05, label: 'Slight ITM', description: '-5%', tooltip: 'Just in the money. A bit of intrinsic value with near-ATM responsiveness.' },
  atm: { anchor_pct: 0.0, label: 'ATM', description: 'At the money', tooltip: 'At the money. The balanced choice — roughly 50 delta on the anchor leg.' },
  'otm-5': { anchor_pct: 0.05, label: 'Slight OTM', description: '+5%', tooltip: 'Just out of the money. Cheaper entry; needs a modest move to pay off.' },
  'otm-10': { anchor_pct: 0.10, label: 'OTM', description: '+10%', tooltip: 'Out of the money. Lower cost, lower probability, bigger percentage payoff.' },
  'otm-20': { anchor_pct: 0.20, label: 'Far OTM', description: '+20%', tooltip: 'Far out of the money. Cheap and speculative — needs a sharp move.' },
};

/**
 * Spread widths, as a fraction of the underlying price rather than a dollar
 * amount, so one preset means the same thing on a $30 stock and a $600 one.
 *
 * Width is the single biggest lever on a spread's character: narrow spreads
 * cost little and cap out fast; wide spreads cost more and behave more like the
 * single option they're built from.
 */
export const spreadWidthPresets = {
  'w-2.5': { width_pct: 0.025, label: 'Very Narrow', description: '2.5%', tooltip: 'Very narrow. Cheapest entry and the fastest to reach max profit, but the smallest total payoff.' },
  'w-5': { width_pct: 0.05, label: 'Narrow', description: '5%', tooltip: 'Narrow. Low cost with a tight profit cap — a good fit for a modest expected move.' },
  'w-10': { width_pct: 0.10, label: 'Balanced', description: '10%', tooltip: 'Balanced width. The usual starting point: meaningful payoff without paying for the full option.' },
  'w-15': { width_pct: 0.15, label: 'Wide', description: '15%', tooltip: 'Wide. Costs more and needs a larger move, but the payoff ceiling is much higher.' },
  'w-20': { width_pct: 0.20, label: 'Very Wide', description: '20%', tooltip: 'Very wide. Behaves close to the outright option — highest cost, highest ceiling.' },
};

/** Exit-speed presets, reusing the screener's targetSpeed map read-only. */
export const spreadSpeedPresets = intermediatePresets.targetSpeed;

/**
 * Flip a strike description's sign for puts, so a preset labelled "-10%" reads
 * as "+10%" when the user is working with puts (ITM/OTM mirror around spot).
 * Same helper the screener's intermediate form applies to its strike cards.
 */
export const adjustDescriptionForPuts = (description, isPut) => {
  if (!isPut || !description) return description;
  if (description.startsWith('-')) return `+${description.slice(1)}`;
  if (description.startsWith('+')) return `-${description.slice(1)}`;
  return description;
};

/**
 * Build the API payload for a spread simulation.
 *
 * Unlike the screener — which lowers each combination into its own scenario
 * object — the spread backend takes the multi-selects directly and expands the
 * Cartesian product itself. That keeps one chain fetch per (ticker x window)
 * instead of one per combination.
 *
 * @param {Object} selections - { expirations: [], anchors: [], widths: [], speedKey, stockMovePct }
 * @param {Array}  tickers    - ticker strings or {value,label} objects
 * @param {string} spreadType - a SPREAD_TYPES key
 * @returns {Object|null} payload for POST /stock-analyzer/spreads/
 */
export const buildSpreadPayload = (selections, tickers, spreadType) => {
  const { expirations = [], anchors = [], widths = [], speedKey = 'balanced', stockMovePct } = selections || {};
  const config = SPREAD_TYPES[spreadType];
  if (!config) return null;

  const tickerValues = formatTickersForAPI(tickers);
  if (!tickerValues.length || !expirations.length || !anchors.length || !widths.length) {
    return null;
  }

  const speed = spreadSpeedPresets[speedKey] || spreadSpeedPresets.balanced;

  // Seed the projected move from the strategy's own bias, the way the screener's
  // intermediate form does. The heatmap is where users actually explore
  // scenarios, so this only sets the initial column.
  const defaultMove = config.direction === 'Bullish' ? 0.20 : -0.20;

  return {
    tickers: tickerValues,
    spread_type: spreadType,
    expiration_keys: expirations,
    anchor_pcts: anchors
      .map((key) => spreadAnchorPresets[key]?.anchor_pct)
      .filter((v) => v !== undefined),
    width_pcts: widths
      .map((key) => spreadWidthPresets[key]?.width_pct)
      .filter((v) => v !== undefined),
    holding_period_type: 'percentage',
    holding_period_value: speed.percent,
    stock_move_pct: stockMovePct != null ? stockMovePct : defaultMove,
  };
};

/** Total combinations a selection will produce — shown on the run button. */
export const countSpreadCombinations = (selections, tickers) => {
  const { expirations = [], anchors = [], widths = [] } = selections || {};
  const tickerCount = formatTickersForAPI(tickers || []).length;
  return tickerCount * expirations.length * anchors.length * widths.length;
};

/**
 * Which of the four verticals two explicit strikes describe.
 *
 * Mirror of `derive_spread_type` in stock_analyzer/services/spreads/pricing.py.
 * The form shows the derived strategy live as soon as both strikes are picked,
 * so this MUST agree with the backend — a badge that disagreed with the row it
 * produced would be worse than no badge.
 *
 * The rule is who owns the leg nearest the money: the lower strike for calls,
 * the higher for puts. Buying it pays a debit, selling it collects a credit.
 *
 * @returns {string|null} a SPREAD_TYPES key, or null when the strikes are
 *   missing or equal (equal strikes are not a spread).
 */
export const deriveSpreadType = (optionType, longStrike, shortStrike) => {
  const type = String(optionType || '').toLowerCase();
  if (type !== 'call' && type !== 'put') return null;

  const long = parseFloat(longStrike);
  const short = parseFloat(shortStrike);
  if (!Number.isFinite(long) || !Number.isFinite(short)) return null;
  if (long === short) return null;

  const longIsNearer = type === 'call' ? long < short : long > short;
  if (type === 'call') return longIsNearer ? 'call_debit' : 'call_credit';
  return longIsNearer ? 'put_debit' : 'put_credit';
};

/** True when a contract row has everything needed to price a spread. */
export const isContractRowComplete = (row) => Boolean(
  row
  && row.ticker
  && row.option_type
  && row.expiration
  && row.long_strike !== '' && row.long_strike != null
  && row.short_strike !== '' && row.short_strike != null
  && deriveSpreadType(row.option_type, row.long_strike, row.short_strike)
);

/**
 * Build the API payload for exact-contract spread pricing.
 *
 * The counterpart to buildSpreadPayload. There is no Cartesian product here —
 * each row is one spread, and the spread type is derived server-side from the
 * strikes rather than sent.
 *
 * @param {Array}  rows     - form rows { ticker, option_type, expiration, long_strike, short_strike }
 * @param {string} speedKey - a spreadSpeedPresets key
 * @returns {Object|null} payload for POST /stock-analyzer/spreads/contracts/
 */
export const buildContractSpreadPayload = (rows, speedKey = 'balanced') => {
  const usable = (rows || []).filter(isContractRowComplete);
  if (!usable.length) return null;

  const speed = spreadSpeedPresets[speedKey] || spreadSpeedPresets.balanced;

  // Seed the projected move from the strategy's bias the way the discovery
  // form does — but only when every row leans the same way. A run that mixes
  // bullish and bearish spreads has no single bias to seed, and the heatmap is
  // where users explore scenarios anyway.
  const directions = new Set(
    usable.map((r) => SPREAD_TYPES[
      deriveSpreadType(r.option_type, r.long_strike, r.short_strike)
    ]?.direction)
  );
  let defaultMove = 0;
  if (directions.size === 1) {
    defaultMove = directions.has('Bullish') ? 0.20 : -0.20;
  }

  return {
    legs: usable.map((r) => ({
      ticker: String(r.ticker).toUpperCase().trim(),
      option_type: r.option_type,
      expiration: r.expiration,
      long_strike: parseFloat(r.long_strike),
      short_strike: parseFloat(r.short_strike),
    })),
    holding_period_type: 'percentage',
    holding_period_value: speed.percent,
    stock_move_pct: defaultMove,
  };
};

/** Human label for a spread row's strike pair, e.g. "$310 / $345". */
export const formatStrikePair = (longStrike, shortStrike) => {
  const fmt = (s) => {
    const n = parseFloat(s);
    if (isNaN(n)) return '-';
    return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
  };
  return `${fmt(longStrike)} / ${fmt(shortStrike)}`;
};
